import { useEffect, useMemo, useState } from "react";
import { Loader2, Shuffle, Ticket, Trophy, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { Link } from "react-router-dom";
import { ArenaHeader } from "@/components/games/GameArena";
import { useJackpot, ACTIVE_PHASES, fmtP } from "@/lib/jackpot";

const Ball = ({ n, hit, size = "w-9 h-9 text-sm" }) => (
  <span className={`${size} rounded-full inline-flex items-center justify-center font-extrabold shadow-sm ${hit ? "pink-gradient text-white" : "bg-white border border-pink-200 text-slate-700"}`}>{n}</span>
);

export function WinnerList({ list, limit = 50 }) {
  if (!list) return null;
  if (list.length === 0) return <p className="text-sm text-slate-400" data-testid="lotto-winners-empty">Diesmal hat niemand 3 oder mehr Richtige.</p>;
  return (
    <ul className="space-y-1.5" data-testid="lotto-winners">
      {list.slice(0, limit).map((w, i) => (
        <li key={`${w.user_id}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`lotto-winner-${i}`}>
          <UserAvatar userId={w.user.user_id} name={w.user.name} picture={w.user.picture} size="w-7 h-7" text="text-xs" crown={false} />
          <UserLink id={w.user.user_id} className="flex-1 min-w-0 truncate font-semibold">{w.user.name}</UserLink>
          <span className="text-xs font-bold text-slate-500">{w.matches} Richtige</span>
          <span className="text-xs font-extrabold text-rose-600 w-20 text-right">+{w.prize.toLocaleString("de-DE")} P.</span>
        </li>
      ))}
    </ul>
  );
}

function useCountdown(at) {
  const [t, setT] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setT(Date.now()), 1000); return () => clearInterval(i); }, []);
  const s = Math.max(0, Math.floor((new Date(at) - t) / 1000));
  return `${Math.floor(s / 86400)}T ${String(Math.floor((s % 86400) / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function Picker({ d, onBought }) {
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const { user, refreshUser } = useAuth();
  const poor = (user?.points ?? 0) < d.price;
  const toggle = (n) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : s.length < 6 ? [...s, n] : s));
  const quick = () => { const a = []; while (a.length < 6) { const n = 1 + Math.floor(Math.random() * 49); if (!a.includes(n)) a.push(n); } setSel(a); };
  const buy = async () => {
    setBusy(true);
    try { await api.post("/lotto/tickets", { numbers: sel }); toast.success(`Tipp abgegeben – viel Glück! (−${d.price} Punkte)`); setSel([]); refreshUser?.(); onBought(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const full = d.tickets.length >= d.max;
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-4" data-testid="lotto-picker">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-xl font-extrabold">Dein Tipp <span className="text-sm font-semibold text-slate-400" data-testid="lotto-selected-count">{sel.length}/6</span></h2>
        <button onClick={quick} disabled={full} data-testid="lotto-quick" className="h-9 px-4 rounded-full border border-pink-200 text-rose-600 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-rose-50 disabled:opacity-50"><Shuffle className="w-4 h-4" /> Quick-Tipp</button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {Array.from({ length: 49 }, (_, i) => i + 1).map((n) => (
          <button key={n} onClick={() => toggle(n)} disabled={full} data-testid={`lotto-num-${n}`} aria-pressed={sel.includes(n)}
            className={`aspect-square rounded-full text-sm font-bold transition-[transform,background-color] active:scale-90 disabled:opacity-40 ${sel.includes(n) ? "pink-gradient text-white scale-105" : "bg-rose-50/60 text-slate-700 hover:bg-rose-100"}`}>{n}</button>
        ))}
      </div>
      <button onClick={buy} disabled={sel.length !== 6 || busy || full || poor} data-testid="lotto-buy" className="w-full h-12 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[.98] transition-transform">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} {full ? `Maximal ${d.max} Tipps pro Woche` : poor ? `Du brauchst ${d.price} Punkte für einen Tipp` : `Tipp abgeben für ${d.price} Punkte`}
      </button>
    </div>
  );
}

function RouletteBonus() {
  const { s } = useJackpot();
  if (!s?.round || !ACTIVE_PHASES.includes(s.phase)) return null;
  return (
    <Link to="/jackpot-roulette" data-testid="lotto-roulette-bonus" className="block rounded-3xl jr-banner p-5 text-white hover:-translate-y-0.5 transition-transform">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-300">{s.phase === "live" ? "Jetzt live · Jackpot Roulette" : "Jackpot Roulette · Do 15:00 – So 23:59"}</p>
      <p className="font-display text-2xl font-black jr-gold-text mt-1">{fmtP(s.round.amount)} Punkte</p>
      <p className="text-xs text-white/75 mt-1">Die Community dreht an diesem Lotto-Jackpot: +1.000, −1.000, ×2 oder BANKROTT. Den kompletten Jackpot holen sich alle mit 6 Richtigen{s.draw_done ? " – Auszahlung Sonntag 23:59" : ""}!</p>
    </Link>
  );
}

export default function Lotto() {
  const [d, setD] = useState(null);
  const load = () => api.get("/lotto").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  const countdown = useCountdown(d?.draw_at || Date.now());
  const lastHits = useMemo(() => new Set(d?.last?.numbers || []), [d]);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="lotto-page">
      <ArenaHeader title="Punkte-Lotto" back="/spiele" />
      <div className="relative overflow-hidden rounded-[2rem] pink-gradient text-white p-6 sm:p-8">
        <p className="text-[11px] font-extrabold tracking-[0.3em] uppercase text-white/80">Punkte-Lotto · 6 aus 49</p>
        <p className="mt-2 text-sm text-white/90">Aktueller Jackpot</p>
        <p className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight" data-testid="lotto-jackpot">{d.jackpot.toLocaleString("de-DE")}<span className="text-xl"> Punkte</span></p>
        <p className="mt-3 text-sm font-semibold flex items-center gap-1.5" data-testid="lotto-countdown"><Clock className="w-4 h-4" /> Ziehung Sonntag 20:00 · noch {countdown}</p>
        <p className="text-xs text-white/80 mt-1">{d.players} Mitspieler diese Woche · Jeder Tipp lässt den Jackpot wachsen</p>
      </div>
      <RouletteBonus />
      <div className="grid grid-cols-4 gap-2 text-center" data-testid="lotto-prizes">
        {[["6", "Jackpot"], ["5", `${d.prizes["5"]} P.`], ["4", `${d.prizes["4"]} P.`], ["3", `${d.prizes["3"]} P.`]].map(([m, p]) => (
          <div key={m} className="rounded-2xl bg-white border border-pink-100 p-3"><p className="text-xs text-slate-500">{m} Richtige</p><p className="font-extrabold text-rose-600 text-sm">{p}</p></div>
        ))}
      </div>
      <Picker d={d} onBought={load} />
      <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-3" data-testid="lotto-my-tickets">
        <h2 className="font-display text-xl font-extrabold">Meine Tipps diese Woche ({d.tickets.length}/{d.max})</h2>
        {d.tickets.length === 0 ? <p className="text-sm text-slate-400">Noch kein Tipp abgegeben.</p>
          : d.tickets.map((t) => <div key={t.id} className="flex flex-wrap gap-1.5" data-testid={`lotto-ticket-${t.id}`}>{t.numbers.map((n) => <Ball key={n} n={n} />)}</div>)}
      </div>
      {d.last && (
        <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-3" data-testid="lotto-last-draw">
          <h2 className="font-display text-xl font-extrabold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Letzte Ziehung ({new Date(d.last.week).toLocaleDateString("de-DE")})</h2>
          <div className="flex flex-wrap gap-2">{d.last.numbers.map((n) => <Ball key={n} n={n} hit size="w-11 h-11 text-base" />)}</div>
          <WinnerList list={d.last.winner_list} />
          {d.last.winners && <p className="text-xs text-slate-500">Gewinner: {["6", "5", "4", "3"].map((m) => `${d.last.winners[m] || 0}× ${m} Richtige`).join(" · ")}</p>}
          {d.last_mine.length > 0 && <div className="space-y-2 pt-2 border-t border-pink-50"><p className="text-sm font-semibold">Deine Tipps</p>
            {d.last_mine.map((t) => <div key={t.id} className="flex flex-wrap items-center gap-1.5">{t.numbers.map((n) => <Ball key={n} n={n} hit={lastHits.has(n)} />)}<span className="text-xs font-bold text-rose-600 ml-1">{t.matches ?? 0} Richtige{t.prize ? ` · +${t.prize} P.` : ""}{t.roulette_prize ? ` · +${fmtP(t.roulette_prize)} Roulette 💎` : ""}</span></div>)}</div>}
        </div>
      )}
    </div>
  );
}
